import {Injectable, OnDestroy} from '@angular/core';
import {Observable, Subject} from 'rxjs';

/**
 * Synchronizes logouts across browser tabs of the same origin via a durable marker in
 * {@code localStorage}.
 *
 * <p>When the user logs out in one tab, {@link broadcastLogout} stores the logout timestamp.
 * Other tabs detect it through:
 * <ul>
 *   <li>the {@code storage} event, which fires instantly in all other <em>active</em> tabs when
 *       localStorage changes, and</li>
 *   <li>the {@code visibilitychange}/{@code focus}/{@code pageshow} events, so tabs that Chrome
 *       froze or discarded because they were inactive for a longer time re-check the marker once
 *       they become active again (a frozen tab receives no events while frozen).</li>
 * </ul>
 *
 * <p>A timestamp (rather than a boolean flag) is used so the marker never has to be cleared: each
 * tab only reacts to logouts that are newer than the one it already knew about when it started.
 * This prevents a freshly logged-in or newly opened tab from logging itself out due to a stale
 * marker. Subscribe to {@link logoutReceived} to react; the originating tab does not react to its
 * own logout.
 */
@Injectable({
  providedIn: 'root'
})
export class LogoutBroadcastService implements OnDestroy {
  /** localStorage key holding the timestamp (ms) of the most recent logout across all tabs. */
  private static readonly LOGOUT_TIMESTAMP_KEY: string = 'awp-auth-logout-timestamp';

  private readonly logoutReceivedSubject: Subject<void> = new Subject<void>();
  /** Emits whenever another tab of the same origin has logged the user out. */
  readonly logoutReceived: Observable<void> = this.logoutReceivedSubject.asObservable();

  /**
   * The highest logout timestamp this tab has already reacted to. Initialized from storage so that
   * a logout that happened before this tab existed does not trigger an immediate logout.
   */
  private lastSeenLogoutTimestamp: number = LogoutBroadcastService.readStoredLogoutTimestamp();

  // Bound handlers kept as fields so they can be removed again in ngOnDestroy.
  private readonly handleStorageEvent = (event: StorageEvent): void => {
    if (event.key === LogoutBroadcastService.LOGOUT_TIMESTAMP_KEY) {
      this.processPotentialLogout();
    }
  };
  private readonly handleTabBecameActive = (): void => this.processPotentialLogout();

  constructor() {
    // Fired in OTHER tabs when localStorage changes; covers active tabs instantly.
    window.addEventListener('storage', this.handleStorageEvent);
    // When a previously inactive/frozen tab becomes active again, re-check whether a logout
    // happened while it could not receive any events.
    document.addEventListener('visibilitychange', this.handleTabBecameActive);
    window.addEventListener('focus', this.handleTabBecameActive);
    window.addEventListener('pageshow', this.handleTabBecameActive);
  }

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.handleStorageEvent);
    document.removeEventListener('visibilitychange', this.handleTabBecameActive);
    window.removeEventListener('focus', this.handleTabBecameActive);
    window.removeEventListener('pageshow', this.handleTabBecameActive);
  }

  /**
   * Notifies other tabs of the same origin that the user has logged out by persisting a durable
   * logout marker. Active tabs react via the {@code storage} event; frozen/inactive tabs react
   * once they become active again.
   */
  broadcastLogout(): void {
    const timestamp: number = Date.now();
    // Remember our own logout first so we never react to it ourselves.
    this.lastSeenLogoutTimestamp = timestamp;
    try {
      localStorage.setItem(LogoutBroadcastService.LOGOUT_TIMESTAMP_KEY, String(timestamp));
    } catch (error: unknown) {
      console.warn('[Webapp] [Auth] Failed to persist logout marker for other tabs.', error);
    }
  }

  /**
   * Emits {@link logoutReceived} if the persisted logout marker is newer than the last logout this
   * tab already reacted to. Using the timestamp as the source of truth deduplicates the multiple
   * signals (storage event, tab re-activation) so the logout is only handled once.
   */
  private processPotentialLogout(): void {
    const timestamp: number = LogoutBroadcastService.readStoredLogoutTimestamp();
    if (timestamp > this.lastSeenLogoutTimestamp) {
      this.lastSeenLogoutTimestamp = timestamp;
      console.log('[Webapp] [Auth] Detected logout from another tab.');
      this.logoutReceivedSubject.next();
    }
  }

  private static readStoredLogoutTimestamp(): number {
    try {
      const raw: string | null = localStorage.getItem(LogoutBroadcastService.LOGOUT_TIMESTAMP_KEY);
      const parsed: number = Number(raw);
      return Number.isFinite(parsed) ? parsed : 0;
    } catch (error: unknown) {
      console.warn('[Webapp] [Auth] Failed to read logout marker from storage.', error);
      return 0;
    }
  }
}

